/**
 * F-296 / T-232-002 — Compact lead-scoring summary embedded in the growth
 * dashboard (ConversionFunnelDashboard). Shows the top 5 scored leads with
 * their 0-100 scores + a link to the full /growth/leads dashboard. Renders
 * nothing while the scoring engine has no data, so an empty scoring engine
 * never disturbs the funnel reading.
 */
import React, { useEffect, useState } from 'react';
import { leadScoringApi, bandOf, clampScore } from '../../services/leadScoring';

const SUMMARY_N = 5;

export const LeadScoringSummary: React.FC = () => {
    const [top, setTop] = useState<Array<{ id: string; name?: string; email?: string; company?: string; segment?: string; score: number }>>([]);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        leadScoringApi
            .getScores()
            .then((leads) => {
                if (cancelled) return;
                const sorted = [...leads].sort((a, b) => clampScore(b.score) - clampScore(a.score)).slice(0, SUMMARY_N);
                setTop(sorted);
            })
            .catch(() => { /* engine not live yet — render nothing */ })
            .finally(() => !cancelled && setReady(true));
        return () => { cancelled = true; };
    }, []);

    if (!ready || top.length === 0) return null;

    return (
        <div className="bg-white rounded-xl shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Top scored leads</h2>
                <a href="/growth/leads" className="text-sm text-green-600 hover:text-green-700 font-medium">
                    Open full dashboard →
                </a>
            </div>
            <ul className="space-y-3" data-testid="ls-summary">
                {top.map((lead) => (
                    <li key={lead.id} data-testid="ls-summary-row">
                        <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700">
                                {lead.name || lead.email || lead.company || lead.id}
                            </span>
                            <span className="text-gray-500">
                                <span className="font-semibold text-gray-800" data-testid="ls-summary-score">{clampScore(lead.score)}</span>
                                {' '}· {bandOf(lead.score)} · {lead.segment || 'segment n/a'}
                            </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded overflow-hidden">
                            <div
                                className={`h-2 rounded ${leadsBarClass(lead.score)}`}
                                style={{ width: `${clampScore(lead.score)}%` }}
                            />
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

function leadsBarClass(score: number): string {
    const band = bandOf(score);
    if (band === 'Hot') return 'bg-green-500';
    if (band === 'Warm') return 'bg-amber-500';
    return 'bg-gray-300';
}

export default LeadScoringSummary;